// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {downloadWithNative, isElectron, openExternalUrl} from '@app/features/ui/utils/NativeUtils';

const logger = new Logger('FileDownloadUtils');
type MediaType = 'image' | 'video' | 'audio' | 'file';
interface EndpointInfo {
	basePath: string;
	origin: string;
}
function parseEndpoint(endpoint: string): EndpointInfo | null {
	if (!endpoint) return null;
	try {
		const parsedEndpoint = new URL(endpoint);
		const basePath =
			parsedEndpoint.pathname.length > 1 && parsedEndpoint.pathname.endsWith('/')
				? parsedEndpoint.pathname.slice(0, -1)
				: parsedEndpoint.pathname || '/';
		return {
			basePath,
			origin: parsedEndpoint.origin,
		};
	} catch {
		return null;
	}
}
function isUrlOnEndpoint(targetUrl: URL, endpoint: EndpointInfo): boolean {
	if (targetUrl.origin !== endpoint.origin) return false;
	if (endpoint.basePath === '/') return true;
	return targetUrl.pathname === endpoint.basePath || targetUrl.pathname.startsWith(`${endpoint.basePath}/`);
}
function appendMediaProxyDownloadParam(src: string): string {
	let parsedSrc: URL;
	try {
		parsedSrc = new URL(src);
	} catch {
		return src;
	}
	const mediaEndpoint = parseEndpoint(RuntimeConfig.mediaEndpoint);
	if (!mediaEndpoint || !isUrlOnEndpoint(parsedSrc, mediaEndpoint)) {
		return src;
	}
	parsedSrc.searchParams.set('download', 'true');
	return parsedSrc.toString();
}
function deriveSuggestedName(src: string, type: MediaType, providedFilename?: string): string {
	if (providedFilename) return providedFilename;
	try {
		const parsed = new URL(src);
		const segments = parsed.pathname.split('/').filter(Boolean);
		const lastSegment = segments[segments.length - 1];
		if (lastSegment) {
			try {
				const decoded = decodeURIComponent(lastSegment);
				if (decoded.includes('.')) return decoded;
				return `${decoded}${defaultExtensionFor(type)}`;
			} catch {
				if (lastSegment.includes('.')) return lastSegment;
				return `${lastSegment}${defaultExtensionFor(type)}`;
			}
		}
	} catch {}
	return `download${defaultExtensionFor(type)}`;
}
function defaultExtensionFor(type: MediaType): string {
	switch (type) {
		case 'image':
			return '.png';
		case 'video':
			return '.mp4';
		case 'audio':
			return '.mp3';
		default:
			return '';
	}
}
export async function downloadFile(src: string, type: MediaType, providedFilename?: string): Promise<void> {
	if (!src) return;
	if (isElectron()) {
		try {
			const outcome = await downloadWithNative({
				url: src,
				suggestedName: deriveSuggestedName(src, type, providedFilename),
			});
			if (outcome === 'success' || outcome === 'canceled') return;
		} catch (error) {
			logger.warn('Native download failed, falling back to browser', error);
		}
	}
	await openExternalUrl(appendMediaProxyDownloadParam(src));
}
export function createSaveHandler(src: string, type: MediaType, providedFilename?: string) {
	return async () => {
		await downloadFile(src, type, providedFilename);
	};
}
